// 📂 frontend/src/components/Toast.tsx
import React, { useEffect, useState } from 'react';
import { useTheme } from '../hooks/useTheme';

export interface ToastProps {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message?: string;
  duration?: number;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ 
  id, 
  type, 
  title, 
  message, 
  duration = 5000, 
  onClose 
}) => {
  const { theme, currentTheme } = useTheme();
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (duration <= 0) return;

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      
      if (remaining === 0) {
        handleClose();
      }
    }, 50);

    return () => clearInterval(interval);
  }, [duration, id]); // Adăugat id în dependencies

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onClose(id), 300);
  };

  const getIcon = () => {
    switch (type) {
      case 'success': return '✅';
      case 'error': return '🚨';
      case 'warning': return '⚠️';
      default: return 'ℹ️';
    }
  };

  const getColor = () => {
    switch (type) {
      case 'success': return theme.colors.success;
      case 'error': return theme.colors.danger;
      case 'warning': return theme.colors.warning;
      default: return theme.colors.primary;
    }
  };

  const styles = {
    toast: {
      position: 'relative' as const,
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '16px',
      backgroundColor: theme.colors.surface,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.medium,
      boxShadow: theme.shadows.large,
      minWidth: '300px',
      maxWidth: '500px',
      marginBottom: '12px',
      transform: isExiting ? 'translateX(400px)' : 'translateX(0)',
      opacity: isExiting ? 0 : 1,
      transition: theme.transitions.normal,
      ...(currentTheme === 'futuristic' && {
        borderColor: getColor() + '44',
        boxShadow: `0 0 20px ${getColor()}33`
      })
    },
    icon: {
      fontSize: '20px',
      flexShrink: 0
    },
    content: {
      flex: 1,
      marginRight: '20px'
    },
    title: {
      margin: '0 0 4px 0',
      fontSize: '14px',
      fontWeight: '600',
      color: theme.colors.text
    },
    message: {
      margin: 0,
      fontSize: '13px',
      color: theme.colors.textSecondary,
      lineHeight: '1.4'
    },
    closeButton: {
      position: 'absolute' as const,
      top: '12px',
      right: '12px',
      padding: '4px',
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      color: theme.colors.textSecondary,
      fontSize: '16px',
      lineHeight: 1,
      opacity: 0.6,
      transition: theme.transitions.fast
    },
    progressBar: {
      position: 'absolute' as const,
      bottom: 0,
      left: 0,
      height: '3px',
      width: `${progress}%`,
      backgroundColor: getColor(),
      borderRadius: '0 0 0 8px',
      transition: 'width 0.05s linear',
      ...(currentTheme === 'futuristic' && {
        boxShadow: `0 0 10px ${getColor()}`
      })
    }
  };

  return (
    <div style={styles.toast} className={currentTheme === 'futuristic' ? 'toast-futuristic' : ''}>
      <span style={styles.icon}>{getIcon()}</span>
      <div style={styles.content}>
        <h4 style={styles.title}>{title}</h4>
        {message && <p style={styles.message}>{message}</p>}
      </div>
      <button
        style={styles.closeButton}
        onClick={handleClose}
        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
        aria-label="Close notification"
      >
        ×
      </button>
      {duration > 0 && <div style={styles.progressBar} />}
    </div>
  );
};

// Container pentru toate toast-urile
export interface ToastContainerProps {
  toasts: Array<{
    id: string;
    type: 'info' | 'success' | 'warning' | 'error';
    title: string;
    message?: string;
    duration?: number;
  }>;
  onClose: (id: string) => void;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ 
  toasts, 
  onClose, 
  position = 'top-right' 
}) => {
  const getPositionStyles = () => {
    const base = {
      position: 'fixed' as const,
      zIndex: 9999,
      pointerEvents: 'none' as const
    };

    switch (position) {
      case 'top-left':
        return { ...base, top: '20px', left: '20px' };
      case 'bottom-right':
        return { ...base, bottom: '20px', right: '20px' };
      case 'bottom-left':
        return { ...base, bottom: '20px', left: '20px' };
      default: // top-right
        return { ...base, top: '20px', right: '20px' };
    }
  };

  return (
    <div style={getPositionStyles()}>
      <div style={{ pointerEvents: 'auto' }}>
        {toasts.map((toast, index) => (
          <Toast
            key={`toast-${toast.id}-${index}`} // Folosim combinație de id și index pentru unicitate
            {...toast}
            onClose={onClose}
          />
        ))}
      </div>
    </div>
  );
};

// Hook pentru a genera ID-uri unice pentru toast-uri
export const useToastId = () => {
  const generateId = () => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };
  
  return { generateId };
};